import {
    Button,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    Box,
    CircularProgress,
    Typography,
    LinearProgress,
    Tooltip,
    Alert,
    useMediaQuery,
    useTheme,
    Card,
    CardContent,
    Divider,
    Chip,
} from "@mui/material";
import MissionLogStyles from "../../../styles/MissionLog.module.css";
import DownloadIcon from  '@mui/icons-material/FileDownloadOutlined';
import ShareIcon from '@mui/icons-material/Share';
import Paper from '@mui/material/Paper';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import FileUpload from "../fileSection/fileUpload";
import HistoryIcon from '@mui/icons-material/History';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import EditIcon from '@mui/icons-material/Edit';
import MapIcon from '@mui/icons-material/Map';
import { hasPermission } from '../../../utils/permissions';

const getStatusClass = (status) => {
    switch (status) {
        case "Authorized":
            return MissionLogStyles.authorized;
        case "Pending":
            return MissionLogStyles.pending;
        case "Rescinded Awaiting":
            return MissionLogStyles.rescinded;
        case "De-authorized":
            return MissionLogStyles.de_authorized;
        case "Complete":
            return MissionLogStyles.complete;
        case "Deleted":
            return MissionLogStyles.deleted;
        case "Never Authorized":
            return MissionLogStyles.pending;
        case "Invalid Detected":
            return MissionLogStyles.de_authorized;
        default:
            return '';
    }
};

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(2)} MB`;
};

const getFileIcon = (fileName) => {
  const ext = fileName.split('.').pop().toLowerCase();
  switch (ext) {
    case 'pdf':
      return <PictureAsPdfIcon color="error" sx={{ mr: 1 }} />;
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'bmp':
    case 'webp':
    case 'tiff':
    case 'tif':
    case 'svg':
      return <ImageIcon color="primary" sx={{ mr: 1 }} />;
    default:
      return <InsertDriveFileIcon sx={{ mr: 1 }} />;
  }
};


export const MoreDetailsComponent = ({
  operation,
  handleCancel,
  handleDelete,
  handleRescind,
  handleInvalidCancel,
  handleCreateTemplate,
  handleLogFlightHours,
  handleEditFlightLog,
  handleDeleteFlightLog,
  handleViewOnMap,
  files,
  handleDownload,
  handleShare,
  handleCheckHistory,
  handleDeleteClick,
  userId,
  file,
  uploading,
  uploadProgress,
  message,
  showPanel,
  completedUploads,
  failedUploads,
  onChooseFile,
  onFileChange,
  onUpload,
  onCancel,
  fileInputRef,
  usedStorage,
  totalStorage,
  hasCloudStorage,
  hasFileShare,
  fileNameExist,
  userRole,
  isInOrganizationMode,
  currentUserProfile,
  canCreateTemplate = true

}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // REGULATORY COMPLIANCE - STRICT PILOT-ONLY MODEL
  // Determine if current user can edit/delete this operation
  // LAANC authorization actions (Cancel, Delete, Rescind, Invalid Cancel) can ONLY be performed
  // by the pilot who created the operation, regardless of organizational role
  // This ensures compliance with FAA/regulator requirements

  const isOwnOperation = operation.user_id === userId;

  // Only the operation owner can modify authorizations (regulatory compliance)
  const canEditOperation = isOwnOperation;
  const canDeleteOperation = isOwnOperation;

  // FILE OPERATION PERMISSIONS
  // Determine if user can upload/delete/share files for this operation
  const canManageFiles = () => {
    // Viewers can never perform file operations (upload/delete/share)
    if (isInOrganizationMode && userRole?.toLowerCase() === 'viewer') {
      return false;
    }

    // All other roles (Pilot, Manager, Admin, Owner) can manage files ONLY on their own operations
    // They can VIEW files on all operations, but can only UPLOAD/DELETE/SHARE on operations they created
    return isOwnOperation;
  };

  const canUploadFiles = canManageFiles();
  const canDeleteFiles = canManageFiles();
  const canShareFiles = canManageFiles();

  // Flight log deletion permissions (Option B):
  // - Owner, Admin, Manager: can delete all flight logs
  // - Pilot: can delete only their own flight logs
  // - Viewer: cannot delete any flight logs
  const canDeleteFlightLog = (flightLog) => {
    if (!isInOrganizationMode || !hasPermission(userRole, 'DELETE_FLIGHT_LOGS')) {
      // Individual mode or no permission
      return false;
    }

    if (['owner', 'admin', 'manager'].includes(userRole?.toLowerCase())) {
      // Owner, Admin, Manager can delete all logs
      return true;
    }

    if (userRole?.toLowerCase() === 'pilot' && flightLog.pilot) {
      // Pilot can only delete their own logs
      return flightLog.pilot.id === currentUserProfile?.userId;
    }

    return false;
  };

return(
  <>
  {/* Role-based access restriction banners */}
  {isInOrganizationMode && !isOwnOperation && (
    <Box sx={{ mb: 2 }}>
      {userRole?.toLowerCase() === 'viewer' ? (
        <Alert severity="info">
          <Typography variant="body2">
            <strong>Viewer Access:</strong> You have read-only access to this operation. You cannot upload, delete, or share files.
          </Typography>
        </Alert>
      ) : userRole?.toLowerCase() === 'pilot' ? (
        <Alert severity="warning">
          <Typography variant="body2">
            <strong>Note:</strong> This operation was created by another pilot. You can view details and files, but cannot upload, delete, or share files.
          </Typography>
        </Alert>
      ) : (
        <Alert severity="info">
          <Typography variant="body2">
            <strong>{userRole?.charAt(0).toUpperCase() + userRole?.slice(1)} Access:</strong> You can view all operation details and files. File upload/delete/share is restricted to the operation creator.
          </Typography>
        </Alert>
      )}
    </Box>
  )}

  <div className={MissionLogStyles.details_container_row}>
    {/* Left column - Operator details */}
    <div className={MissionLogStyles.summary_operator_details}>
      <div className={MissionLogStyles.operator_name}>
        <p>
          Operator: <br />
        </p>
        <span>{operation.operator_name}</span> <br />
        (ID:<span>{operation.operator_id}</span>)
      </div>

      <div className={MissionLogStyles.operator_id}>
        <p>
          Operation ID: <br />
        </p>
        <span>FLZ{operation.operation_id}</span>
      </div>

      <div className={MissionLogStyles.operator_timezone}>
        <p>
          Operation Timezone: <br />
        </p>
        <span>{operation.timezone}</span>
      </div>

      {/* Mobile only - Action buttons below operator details */}
      {isMobile && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
          {/* File Upload */}
          <Tooltip
            title={!canUploadFiles ? (
              userRole?.toLowerCase() === 'viewer'
                ? "Viewers cannot upload files"
                : "You can only upload files to your own operations"
            ) : ""}
            arrow
          >
            <span style={{ display: 'block' }}>
              <FileUpload
                file={file}
                uploading={uploading}
                uploadProgress={uploadProgress}
                message={message}
                showPanel={showPanel}
                completedUploads={completedUploads}
                failedUploads={failedUploads}
                onChooseFile={canUploadFiles ? onChooseFile : null}
                onFileChange={canUploadFiles ? onFileChange : null}
                onUpload={canUploadFiles ? onUpload : null}
                fileNameExist={fileNameExist}
                onCancel={onCancel}
                fileInputRef={fileInputRef}
                hasCloudStorage={hasCloudStorage && canUploadFiles}
                fullWidth
                disabled={!canUploadFiles}
              />
            </span>
          </Tooltip>

          {/* Create Template */}
          {handleCreateTemplate && (
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              size="small"
              sx={{ fontSize: '0.813rem' }}
              startIcon={<BookmarkAddIcon sx={{ fontSize: 18 }} />}
              onClick={() => handleCreateTemplate(operation)}
              disabled={!canCreateTemplate}
            >
              Create Template
            </Button>
          )}

          {/* Log Flight */}
          {handleLogFlightHours && (
            <Button
              variant="outlined"
              color="success"
              fullWidth
              size="small"
              sx={{ fontSize: '0.813rem' }}
              startIcon={<FlightTakeoffIcon sx={{ fontSize: 18 }} />}
              onClick={() => handleLogFlightHours(operation)}
            >
              Log Flight
            </Button>
          )}

          {/* View on Map */}
          {handleViewOnMap && (
            <Button
              variant="outlined"
              color="info"
              fullWidth
              size="small"
              sx={{ fontSize: '0.813rem' }}
              startIcon={<MapIcon sx={{ fontSize: 18 }} />}
              onClick={() => handleViewOnMap(operation)}
            >
              View on Map (Beta)
            </Button>
          )}
        </Box>
      )}
    </div>

    {/* Right column - Desktop only Action buttons */}
    {!isMobile && (
      <div className={MissionLogStyles.summary_action_buttons}>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'stretch' }}>
          {/* File Upload */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Tooltip
              title={!canUploadFiles ? (
                userRole?.toLowerCase() === 'viewer'
                  ? "Viewers cannot upload files"
                  : "You can only upload files to your own operations"
              ) : ""}
              arrow
            >
              <span style={{ display: 'block', height: '100%' }}>
                <FileUpload
                  file={file}
                  uploading={uploading}
                  uploadProgress={uploadProgress}
                  message={message}
                  showPanel={showPanel}
                  completedUploads={completedUploads}
                  failedUploads={failedUploads}
                  onChooseFile={canUploadFiles ? onChooseFile : null}
                  onFileChange={canUploadFiles ? onFileChange : null}
                  onUpload={canUploadFiles ? onUpload : null}
                  fileNameExist={fileNameExist}
                  onCancel={onCancel}
                  fileInputRef={fileInputRef}
                  hasCloudStorage={hasCloudStorage && canUploadFiles}
                  fullWidth
                  disabled={!canUploadFiles}
                />
              </span>
            </Tooltip>
          </Box>

          {/* Create Template */}
          {handleCreateTemplate && (
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                sx={{ height: '100%', minHeight: 36.5 }}
                startIcon={<BookmarkAddIcon />}
                onClick={() => handleCreateTemplate(operation)}
                disabled={!canCreateTemplate}
              >
                Create Template
              </Button>
            </Box>
          )}

          {/* View on Map */}
          {handleViewOnMap && (
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Button
                variant="outlined"
                color="info"
                fullWidth
                sx={{ height: '100%', minHeight: 36.5 }}
                startIcon={<MapIcon />}
                onClick={() => handleViewOnMap(operation)}
              >
                View on Map (Beta)
              </Button>
            </Box>
          )}

          {/* Log Flight */}
          {handleLogFlightHours && (
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Button
                variant="outlined"
                color="success"
                fullWidth
                sx={{ height: '100%', minHeight: 36.5 }}
                startIcon={<FlightTakeoffIcon />}
                onClick={() => handleLogFlightHours(operation)}
              >
                Log Flight
              </Button>
            </Box>
          )}
        </Box>
      </div>
    )}
  </div>

    {/* Flight logs table — shown when one or more flight logs exist */}
    {operation.flightLogs?.length > 0 && (
      <Box
        sx={{
          mt: 1,
          borderRadius: 1,
          border: '1px solid #c8e6c9',
          overflow: 'hidden',
        }}
      >
        {/* Table header - hidden on mobile */}
        {!isMobile && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 60px 80px 100px',
              px: 1.5,
              py: 0.5,
              bgcolor: '#e8f5e9',
              borderBottom: '1px solid #c8e6c9',
            }}
          >
            {['Date', 'Drone', 'Hrs', 'Pilot', 'Actions'].map((h) => (
              <Typography key={h} variant="caption" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                {h}
              </Typography>
            ))}
          </Box>
        )}

        {/* Table rows - grid on desktop, cards on mobile */}
        {operation.flightLogs.map((log, index) => (
          isMobile ? (
            // Mobile Card Layout
            <Box
              key={log.id}
              sx={{
                px: 1.5,
                py: 1,
                borderBottom: index < operation.flightLogs.length - 1 ? '1px solid #f0f0f0' : 'none',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                  {new Date(log.flightDate).toLocaleDateString('en-US', {
                    month: 'short', day: 'numeric', year: 'numeric'
                  })}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {log.flightDuration}h
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {log.drone
                  ? (log.drone.nickname || `${log.drone.manufacturer} ${log.drone.model}`)
                  : '—'}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Pilot: {log.pilot ? `${log.pilot.firstName} ${log.pilot.lastName}` : '—'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {handleEditFlightLog && (
                    <Tooltip title="Edit" arrow>
                      <IconButton
                        size="small"
                        onClick={() => handleEditFlightLog(operation, log)}
                        sx={{ color: '#1976d2', p: 0.5 }}
                      >
                        <EditIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {handleDeleteFlightLog && canDeleteFlightLog(log) && (
                    <Tooltip title="Delete" arrow>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteFlightLog(operation, log)}
                        sx={{ color: '#d32f2f', p: 0.5 }}
                      >
                        <DeleteOutlinedIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </Box>
          ) : (
            // Desktop Grid Layout
            <Box
              key={log.id}
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 60px 80px 100px',
                px: 1.5,
                py: 0.75,
                alignItems: 'center',
                borderBottom: '1px solid #f0f0f0',
                '&:last-child': { borderBottom: 'none' },
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {new Date(log.flightDate).toLocaleDateString('en-US', {
                  month: 'short', day: 'numeric', year: 'numeric'
                })}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {log.drone
                  ? (log.drone.nickname || `${log.drone.manufacturer} ${log.drone.model}`)
                  : '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {log.flightDuration}h
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {log.pilot ? `${log.pilot.firstName} ${log.pilot.lastName}` : '—'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {handleEditFlightLog && (
                  <Tooltip title="Edit flight log" arrow>
                    <IconButton
                      size="small"
                      onClick={() => handleEditFlightLog(operation, log)}
                      sx={{ color: '#1976d2', p: 0.5 }}
                    >
                      <EditIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {handleDeleteFlightLog && canDeleteFlightLog(log) && (
                  <Tooltip title="Delete flight log" arrow>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteFlightLog(operation, log)}
                      sx={{ color: '#d32f2f', p: 0.5 }}
                    >
                      <DeleteOutlinedIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          )
        ))}
      </Box>
    )}

    <div className={MissionLogStyles.authorization_container}>
      <Typography
        className={MissionLogStyles.authorization_table_title}
        sx={{
          mb: isMobile ? 1.5 : 2,
          fontSize: { xs: '16px', sm: '20px' }
        }}
      >
        Authorizations
      </Typography>

      {isMobile ? (
        // Mobile Card Layout
        operation.authorizations && operation.authorizations.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {operation.authorizations.map((authorization) => (
              <Card
                key={authorization.authorization_id}
                variant="outlined"
                sx={{ borderRadius: 2 }}
              >
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      FLZ{authorization.authorization_id}
                    </Typography>
                    <Chip
                      label={authorization.status}
                      size="small"
                      className={getStatusClass(authorization.status)}
                      sx={{ fontSize: '0.7rem' }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">Facility:</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        {authorization.authorizing_airport_code}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">Start Time:</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        {authorization.authorization_start_time}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">Duration:</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        {authorization.duration} minutes
                      </Typography>
                    </Box>
                  </Box>

                  {/* Action Button */}
                  {authorization.status === "Authorized" && canEditOperation && (
                    <Button
                      fullWidth
                      size="small"
                      className={MissionLogStyles.de_authorized_button}
                      onClick={() => handleCancel(authorization.authorization_id)}
                    >
                      Cancel
                    </Button>
                  )}
                  {(authorization.status === "De-authorized" || authorization.status === "Complete") && canDeleteOperation && (
                    <Button
                      fullWidth
                      size="small"
                      className={MissionLogStyles.de_authorized_button}
                      onClick={() => handleDelete(authorization.authorization_id)}
                    >
                      Delete
                    </Button>
                  )}
                  {authorization.status === "Rescinded Awaiting" && canEditOperation && (
                    <Button
                      fullWidth
                      size="small"
                      className={MissionLogStyles.de_authorized_button}
                      onClick={() => handleRescind(authorization.authorization_id)}
                    >
                      Rescind
                    </Button>
                  )}
                  {authorization.status === "Invalid Detected" && canEditOperation && (
                    <Button
                      fullWidth
                      size="small"
                      variant="contained"
                      onClick={() => handleInvalidCancel(authorization.authorization_id)}
                    >
                      Invalid Cancel
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : !operation.authorizations ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body2" color="textSecondary">
              No authorizations required. This operation is in an area that does not require LAANC authorization.
            </Typography>
          </Box>
        ) : null
      ) : (
        // Desktop Table Layout
        <TableContainer className={MissionLogStyles.authorization_table_wrapper}>
          <Table className={MissionLogStyles.authorization_table}>
            <TableHead className={MissionLogStyles.authorization_table_header}>
              <TableRow className={MissionLogStyles.authorization_table_row_header}>
                <TableCell className={MissionLogStyles.authorization_table_cell_header}>
                  Authorization ID
                </TableCell>
                <TableCell className={MissionLogStyles.authorization_table_cell_header}>
                  Facility
                </TableCell>
                <TableCell className={MissionLogStyles.authorization_table_cell_header}>
                  Start Time
                </TableCell>
                <TableCell className={MissionLogStyles.authorization_table_cell_header}>
                  Duration
                </TableCell>
                <TableCell className={MissionLogStyles.authorization_table_cell_header}>
                  Status
                </TableCell>
                <TableCell className={MissionLogStyles.authorization_table_cell_header}>
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {operation.authorizations && operation.authorizations.length > 0 ? (
                operation.authorizations.map((authorization) => (
                  <TableRow
                    key={authorization.authorization_id}
                    className={MissionLogStyles.authorization_table_row}
                  >
                    <TableCell className={MissionLogStyles.authorization_table_row}>
                      FLZ{authorization.authorization_id}
                    </TableCell>
                    <TableCell className={MissionLogStyles.authorization_table_row}>
                      <span>{authorization.authorizing_airport_code}</span>
                    </TableCell>
                    <TableCell className={MissionLogStyles.authorization_table_row}>
                      <span>{authorization.authorization_start_time}</span>
                    </TableCell>
                    <TableCell className={MissionLogStyles.authorization_table_row}>
                      {authorization.duration} minutes
                    </TableCell>
                    <TableCell className={MissionLogStyles.authorization_table_row}>
                      <span
                        className={`${getStatusClass(authorization.status)} ${
                          MissionLogStyles.status_badge
                        }`}
                      >
                        {authorization.status}
                      </span>
                    </TableCell>
                    <TableCell className={MissionLogStyles.authorization_table_row}>
                      {authorization.status === "Authorized" && canEditOperation && (
                        <Button
                          className={MissionLogStyles.de_authorized_button}
                          onClick={() => handleCancel(authorization.authorization_id)}
                        >
                          Cancel
                        </Button>
                      )}
                      {(authorization.status === "De-authorized" || authorization.status === "Complete") && canDeleteOperation && (
                        <Button
                          className={MissionLogStyles.de_authorized_button}
                          onClick={() => handleDelete(authorization.authorization_id)}
                        >
                          Delete
                        </Button>
                      )}
                      {authorization.status === "Rescinded Awaiting" && canEditOperation && (
                        <Button
                          className={MissionLogStyles.de_authorized_button}
                          onClick={() => handleRescind(authorization.authorization_id)}
                        >
                          Rescind
                        </Button>
                      )}
                      {authorization.status === "Invalid Detected" && canEditOperation && (
                        <Button
                          size="small"
                          onClick={() => handleInvalidCancel(authorization.authorization_id)}
                          variant="contained"
                        >
                          Invalid Cancel
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : !operation.authorizations ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                    className={MissionLogStyles.authorization_table_row}
                    style={{ padding: '24px', color: '#666' }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      No authorizations required. This operation is in an area that does not require LAANC authorization.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>

    <div className={MissionLogStyles.additional_table_container}>
      {/* Header with storage */}
      <Box sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'flex-start' : 'center',
        mb: isMobile ? 1.5 : 2,
        gap: isMobile ? 1 : 0
      }}>
        <Typography
          className={MissionLogStyles.authorization_table_title}
          sx={{ fontSize: { xs: '16px', sm: '20px' } }}
        >
          Your Files
        </Typography>
        <Box sx={{ width: isMobile ? '100%' : '200px', position: 'relative' }}>
          <LinearProgress
            variant="determinate"
            value={(usedStorage / totalStorage) * 100}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: '#ebeffc',
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
                backgroundColor: '#0277bd',
              },
            }}
          />
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mt: 0.5
          }}>
            <Typography variant="caption" color="text.secondary">
              {usedStorage}GB
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {totalStorage}GB
            </Typography>
          </Box>
        </Box>
      </Box>

      {isMobile ? (
        // Mobile Card Layout
        files && files.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {files.map((file) => (
              <Card
                key={file.fileId}
                variant="outlined"
                sx={{ borderRadius: 2 }}
              >
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  {/* File name and icon */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    {getFileIcon(file.fileName)}
                    <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, wordBreak: 'break-word' }}>
                      {file.fileName.length > 30
                        ? `${file.fileName.split('.').slice(0, -1).join('.').slice(0, 25)}...${file.fileName.split('.').pop()}`
                        : file.fileName
                      }
                    </Typography>
                  </Box>

                  {/* File details */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Size: <strong>{formatFileSize(file.fileSize)}</strong>
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Uploaded: <strong>{file.createdAt}</strong>
                      </Typography>
                    </Box>
                  </Box>

                  {/* Actions */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                    <Tooltip title="Download" arrow>
                      <IconButton
                        size="small"
                        onClick={() => handleDownload(file, userId)}
                        sx={{ color: '#1976d2' }}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>

                    <Tooltip
                      title={
                        !canShareFiles
                          ? (userRole?.toLowerCase() === 'viewer'
                            ? "Viewers cannot share files"
                            : "You can only share files from your own operations")
                          : (!hasFileShare ? "File share not available in your current plan" : "Share")
                      }
                      arrow
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={!hasFileShare || !canShareFiles}
                          onClick={() => handleShare(file)}
                          sx={{ color: '#1976d2' }}
                        >
                          <ShareIcon />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip
                      title={!hasCloudStorage ? "File share not available in your current plan" : "History"}
                      arrow
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={!hasFileShare}
                          onClick={() => handleCheckHistory(file)}
                          sx={{ color: '#1976d2' }}
                        >
                          <HistoryIcon />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip
                      title={
                        !canDeleteFiles
                          ? (userRole?.toLowerCase() === 'viewer'
                            ? "Viewers cannot delete files"
                            : "You can only delete files from your own operations")
                          : "Delete file"
                      }
                      arrow
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={!canDeleteFiles}
                          onClick={() => handleDeleteClick(file.fileName)}
                          sx={{ color: '#d32f2f' }}
                        >
                          <DeleteOutlinedIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body2" color="textSecondary">
              No files uploaded yet
            </Typography>
          </Box>
        )
      ) : (
        // Desktop Table Layout
        <TableContainer className={MissionLogStyles.authorization_table_wrapper}>
          <Table className={MissionLogStyles.authorization_table}>
            <TableHead className={MissionLogStyles.authorization_table_header}>
              <TableRow className={MissionLogStyles.authorization_table_row_header}
            >
              <TableCell
                className={MissionLogStyles.authorization_table_cell_header}
              >
              File Name
              </TableCell>
              <TableCell
                className={MissionLogStyles.authorization_table_cell_header}
              >
              Size (MB)
              </TableCell>
              <TableCell
                className={MissionLogStyles.authorization_table_cell_header}
              >
              Uploaded Date
              </TableCell>
              <TableCell
                className={MissionLogStyles.authorization_table_cell_header}
              >
              Actions
              </TableCell>

            </TableRow>
          </TableHead>
          <TableBody>
            {files?.map((file) => (
              <TableRow
                key={file.fildId}
                className={MissionLogStyles.authorization_table_row}
              >
              <TableCell className={MissionLogStyles.authorization_table_row}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {getFileIcon(file.fileName)}
                <span style={{ marginLeft: '0.5rem' }}>
                  {
                    file.fileName.length > 10 ? `${file.fileName.split('.').slice(0, -1).join('.').slice(0, 14)}...${file.fileName.split('.').pop()}` : file.fileName
                  }
                </span>
              </div>
            </TableCell>

                <TableCell className={MissionLogStyles.authorization_table_row}>
                  <span>{formatFileSize(file.fileSize)}</span>
                </TableCell>
                <TableCell className={MissionLogStyles.authorization_table_row}>
                  <span>{file.createdAt}</span>
                </TableCell>
                <TableCell className={MissionLogStyles.authorization_table_row}>
                <IconButton  onClick={() => handleDownload(file,userId)}>
                <DownloadIcon   />
              </IconButton>

               {/*<IconButton disabled={!hasFileShare} onClick={() => handleCheckHistory(file.fileId)} title={hasFileShare ? "File Share History" : "File share history not available in your current plan"}>*/}

                  <Tooltip
                    title={
                      !canShareFiles
                        ? (userRole?.toLowerCase() === 'viewer'
                          ? "Viewers cannot share files"
                          : "You can only share files from your own operations")
                        : (!hasFileShare ? "File share not available in your current plan" : "")
                    }
                    arrow
                  >
                    <span> {/* Wrapping span for tooltip to work on disabled button */}
                      <IconButton
                        disabled={!hasFileShare || !canShareFiles}
                        onClick={() => handleShare(file)}
                      >
                        <ShareIcon />
                      </IconButton>
                    </span>
                  </Tooltip>


                   <Tooltip
                       title={!hasCloudStorage ? "File share not available in your current plan" : ""}
                       arrow
                   >
                   <span>
               <IconButton  disabled={!hasFileShare} onClick={() => handleCheckHistory(file)}>
                <HistoryIcon />
              </IconButton>
                   </span>
                     </Tooltip>

              <Tooltip
                title={
                  !canDeleteFiles
                    ? (userRole?.toLowerCase() === 'viewer'
                      ? "Viewers cannot delete files"
                      : "You can only delete files from your own operations")
                    : "Delete file"
                }
                arrow
              >
                <span>
                  <IconButton
                    disabled={!canDeleteFiles}
                    onClick={() => handleDeleteClick(file.fileName)}
                  >
                    <DeleteOutlinedIcon />
                  </IconButton>
                </span>
              </Tooltip>
                </TableCell>

              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      )}
    </div>
  </>
          )};
